/**
 * RFC-4180 CSV, parsed in the browser.
 *
 * A statement file never leaves the machine (spec ruling 2): the browser reads
 * it, this parses it, and only normalised transaction lines are sent up. So
 * nothing here may touch a Node or Worker API — no fs, no Buffer, no crypto —
 * or the same code could not run in both the browser and vitest.
 *
 * Ported from StableBooks' src/main/pricelist.ts, which had exactly this
 * parser wrapped in a filesystem reader. The wrapper is gone; the parser is
 * unchanged, because it is already dependency-free and already right about the
 * cases that bite: a comma inside quotes, a newline inside quotes, "" as an
 * escaped quote, and CRLF.
 */

/**
 * Every field of every row, as text. Quoted fields keep their commas and
 * newlines; `""` becomes one `"`. CRLF, bare LF and bare CR all end a row.
 * Returns [] for empty input.
 */
export function parseCsv(text: string): string[][] {
  if (text.length === 0) return [];

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const n = text.length;

  const endField = (): void => {
    row.push(field);
    field = '';
  };
  const endRow = (): void => {
    endField();
    rows.push(row);
    row = [];
  };

  while (i < n) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (c === ',') {
      endField();
      i++;
      continue;
    }
    if (c === '\r') {
      // CRLF or a lone CR both terminate the row.
      if (text[i + 1] === '\n') i++;
      endRow();
      i++;
      continue;
    }
    if (c === '\n') {
      endRow();
      i++;
      continue;
    }
    field += c;
    i++;
  }

  // A trailing field or row that no newline terminated.
  if (field.length > 0 || row.length > 0) endRow();

  return rows;
}

/**
 * The rows a bank export actually carries: BOM stripped, blank lines dropped.
 *
 * Excel and most bank download buttons write a UTF-8 BOM, and an invisible
 * U+FEFF glued to the first header cell is enough to make 'Date' fail to match
 * a header sniff. Blank rows go because a trailing newline, or the empty line
 * banks like to leave between a header block and the transactions, is not a
 * transaction and must not become one.
 */
export function parseCsvRows(text: string): string[][] {
  const body = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  return parseCsv(body).filter((row) => row.some((cell) => cell.trim() !== ''));
}
